/**
 * L2 for the pinpoint board: one finding's full evidence, in a `Drawer` keyed by
 * `?finding=<id>` so the view is shareable and survives a refresh (design rules
 * §1 / §4.2).
 *
 * The reason this drawer exists at all is the **capture pack**: the activity ids
 * are the handle Microsoft support and HSO ask for, and copying five GUIDs out
 * of a table by hand is where a good finding goes to die. So the ids are one
 * click for all of them, and one click each — the same affordance the record
 * drawer already gives the Performance Timer URL.
 *
 * Every evidence list can legitimately be empty, and empty is stated rather than
 * left blank: "the evidence query found nothing for this cell" is a different
 * fact from "this finding is unfounded", and a blank panel would read as the
 * second. Nothing here is ever filled in with a plausible id.
 *
 * ## The measurements, above the capture pack
 *
 * Between the verdict and the ids sit the three views a ranked row cannot carry
 * (`./finding-charts`): the cell's own distribution against its pooled peers,
 * a dot per company on the same form, and the median by hour of day. They
 * answer the questions someone asks before opening a ticket — is the whole form
 * slow or a few sessions, is this company an outlier or is the form dear
 * everywhere, is it the load or the work — and they come from a second route
 * fetched only once this drawer is open, so a board nobody drilled into costs
 * nothing extra. They sit above the ids because the ids are what you send once
 * you have decided; the measurements are how you decide.
 */

import { Copy, Icon } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  type DescriptionListEntry,
  EmptyState,
  Typography,
  useToastManager,
} from "@norican/react";
import type { ReactNode } from "react";

import { DetailDrawer } from "@/components/board";
import type { FoConsoleLookback, FoConsolePinpointFinding } from "@/lib/api";
import { logger } from "@/lib/logger";

import { PinpointKindChip, PinpointVerdictChip } from "./console-chips";
import {
  EM_DASH,
  EXCESS_HOURS_MEANING,
  formatDurationMs,
  formatExcessHours,
  formatRatio,
  lookbackOption,
  PINPOINT_KIND_DETAIL,
  pinpointKind,
} from "./console-format";
import { FindingCharts } from "./finding-charts";
import { formatCount } from "./status";

/** What an empty evidence list means. Never "no problem" — the query found nothing. */
const EMPTY_EVIDENCE_NOTE =
  "An empty list means the evidence query returned nothing for this cell in this window, not that " +
  "the finding is unfounded. Nothing is filled in.";

interface PinpointDrawerProps {
  /** The finding whose evidence is open, or `null` when the drawer is closed. */
  finding: FoConsolePinpointFinding | null;
  /** The board's window, restated in the drawer so a shared link explains itself. */
  lookback: FoConsoleLookback;
  onClose: () => void;
  /** The drill path back up; the finding's title is appended as the current crumb. */
  breadcrumbs: { label: string; to?: string }[];
}

export function PinpointDrawer({ finding, lookback, onClose, breadcrumbs }: PinpointDrawerProps) {
  if (finding == null) {
    return null;
  }
  return (
    <PinpointDrawerBody
      finding={finding}
      lookback={lookback}
      onClose={onClose}
      breadcrumbs={breadcrumbs}
    />
  );
}

interface PinpointDrawerBodyProps extends Omit<PinpointDrawerProps, "finding"> {
  finding: FoConsolePinpointFinding;
}

function PinpointDrawerBody({ finding, lookback, onClose, breadcrumbs }: PinpointDrawerBodyProps) {
  const toast = useToastManager();
  const evidence = finding.evidence;
  const kind = pinpointKind(finding.kind);

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.add({ title: "Copied", description: `${what} is on the clipboard.`, type: "success" });
    } catch (error) {
      logger.warn("[fo-console] pinpoint clipboard write failed", error);
      toast.add({ title: "Couldn't copy", description: text, type: "error", timeout: 0 });
    }
  };

  const items: DescriptionListEntry[] = [
    { label: "Kind", value: <PinpointKindChip kind={finding.kind} size="sm" /> },
    { label: "Verdict", value: <PinpointVerdictChip verdict={finding.verdict} size="sm" /> },
    {
      label: "Form",
      // An `aos_node` finding names no form; the em dash says so rather than
      // rendering an empty cell that reads as "all forms".
      value: finding.form === "" ? EM_DASH : <span className="break-all">{finding.form}</span>,
    },
    {
      label: "Company",
      value:
        finding.company === "" ? "every company" : `${finding.company_name} (${finding.company})`,
    },
    { label: "Window", value: lookbackOption(lookback).caption },
    { label: "People affected", value: formatCount(finding.users) },
    { label: "Form loads", value: formatCount(finding.events) },
    { label: "Median load", value: formatDurationMs(finding.p50_ms) },
    { label: "P95 load", value: formatDurationMs(finding.p95_ms) },
    { label: "Peer median", value: formatDurationMs(finding.peer_p50_ms) },
    { label: "Against peers", value: formatRatio(finding.ratio) },
    { label: "Waiting above peers", value: formatExcessHours(finding.excess_hours) },
    { label: "Worst hour (UTC)", value: evidence.worst_hour ?? EM_DASH },
  ];

  return (
    <DetailDrawer
      open
      onClose={onClose}
      title={finding.title}
      breadcrumbs={breadcrumbs}
      items={items}
    >
      <Alert tone="info" title="What to do">
        {finding.next_step}
      </Alert>

      <Section title="Why this verdict">
        <Typography variant="body-sm">{finding.verdict_reason}</Typography>
        <Typography variant="caption" tone="muted">
          {PINPOINT_KIND_DETAIL[kind]}. {EXCESS_HOURS_MEANING}
        </Typography>
      </Section>

      {/* The four measurements behind the row, fetched on open. They bring their
          own section headings in this drawer's grammar, their own null states
          and their own access/error panels, because each of the four queries can
          fail on its own and one failure must not blank the other three. */}
      <FindingCharts finding={finding} lookback={lookback} />

      <Section
        title={`Activity ids (${formatCount(evidence.activity_ids.length)})`}
        action={
          evidence.activity_ids.length > 0 ? (
            <Button
              variant="outline"
              tone="neutral"
              size="sm"
              startIcon={<Icon icon={Copy} size="xs" />}
              onClick={() => void copy(evidence.activity_ids.join("\n"), "The capture pack")}
            >
              Copy all {evidence.activity_ids.length}
            </Button>
          ) : undefined
        }
      >
        {evidence.activity_ids.length === 0 ? (
          <EmptyState
            size="sm"
            title="No activity id attached"
            description={`The evidence query reads loads over 10 s only, and found none for this cell in the ${lookbackOption(lookback).caption}. ${EMPTY_EVIDENCE_NOTE}`}
          />
        ) : (
          <>
            <ul className="flex flex-col gap-1">
              {evidence.activity_ids.map((activityId) => (
                <li key={activityId} className="flex flex-wrap items-center gap-2">
                  <code className="text-fg text-xs break-all">{activityId}</code>
                  <Button
                    variant="ghost"
                    tone="neutral"
                    size="sm"
                    iconOnly
                    aria-label={`Copy activity id ${activityId}`}
                    onClick={() => void copy(activityId, "The activity id")}
                  >
                    <Icon icon={Copy} size="xs" />
                  </Button>
                </li>
              ))}
            </ul>
            <Typography variant="caption" tone="muted">
              These are the slowest loads behind the finding, over 10 s each. They are what a
              Microsoft support case or an HSO ticket is opened with.
            </Typography>
          </>
        )}
      </Section>

      <Section title={`AOS nodes (${formatCount(evidence.aos_nodes.length)})`}>
        {evidence.aos_nodes.length === 0 ? (
          <Typography variant="body-sm" tone="muted">
            No AOS instance attached to those loads. {EMPTY_EVIDENCE_NOTE}
          </Typography>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {evidence.aos_nodes.map((node) => (
              <Badge key={node} tone="neutral" variant="outline" size="xs" shape="rounded">
                {node}
              </Badge>
            ))}
          </div>
        )}
      </Section>

      <Section title={`Exception signatures (${formatCount(evidence.exceptions.length)})`}>
        {evidence.exceptions.length === 0 ? (
          <Typography variant="body-sm" tone="muted">
            No X++ exception was raised in those activities. {EMPTY_EVIDENCE_NOTE}
          </Typography>
        ) : (
          <>
            <ul className="flex flex-col gap-1">
              {evidence.exceptions.map((signature) => (
                <li key={signature}>
                  <code className="text-fg text-xs break-all">{signature}</code>
                </li>
              ))}
            </ul>
            <Typography variant="caption" tone="muted">
              Class and method only — no call stack and no message, so nothing customer data could
              be embedded in leaves the workspace.
            </Typography>
          </>
        )}
      </Section>
    </DetailDrawer>
  );
}

interface SectionProps {
  title: string;
  /** A control on the section's header row (the copy-all button). */
  action?: ReactNode;
  children: ReactNode;
}

function Section({ title, action, children }: SectionProps) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Typography
          variant="caption"
          tone="muted"
          as="h3"
          className="font-semibold tracking-wide uppercase"
        >
          {title}
        </Typography>
        {action}
      </div>
      {children}
    </section>
  );
}
